<script>
import Modal from 'dashboard/components/Modal.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

const ENTERPRISE_PLAN = 'flip-empresarial';

const isPresent = value => value !== null && value !== undefined;

const formatValue = value => {
  if (!isPresent(value)) return '-';

  return value.toLocaleString ? value.toLocaleString('pt-BR') : value;
};

export default {
  name: 'BillingLimitModal',
  components: {
    Modal,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    entitlement: {
      type: Object,
      default: null,
    },
    accountId: {
      type: [Number, String],
      default: null,
    },
  },
  computed: {
    entitlementData() {
      return this.entitlement || {};
    },
    featureLabel() {
      return (
        this.entitlementData.label ||
        this.entitlementData.featureLabel ||
        this.entitlementData.feature ||
        this.entitlementData.key ||
        'recurso'
      );
    },
    state() {
      return this.entitlementData.state;
    },
    isExpired() {
      return this.state === 'expired';
    },
    isEnterpriseSuggestion() {
      return this.suggestedUpgradePlan === ENTERPRISE_PLAN;
    },
    suggestedUpgradePlan() {
      return this.entitlementData.suggestedUpgradePlan;
    },
    suggestedUpgradePlanLabel() {
      return this.entitlementData.suggestedUpgradePlanLabel;
    },
    hasSuggestedUpgrade() {
      return !!this.suggestedUpgradePlan;
    },
    title() {
      if (this.isExpired) {
        return 'Faturamento precisa de atencao';
      }

      if (this.state === 'blocked') {
        return 'Recurso indisponivel no plano atual';
      }

      return `Limite de ${this.featureLabel} atingido`;
    },
    message() {
      if (this.entitlementData.message) {
        return this.entitlementData.message;
      }

      if (this.isExpired) {
        return 'Regularize o faturamento para voltar a operar este recurso.';
      }

      if (this.state === 'blocked') {
        return 'Este recurso exige um plano compativel.';
      }

      return 'A conta chegou ao limite disponivel para este recurso.';
    },
    usageLabel() {
      return formatValue(this.entitlementData.usage);
    },
    usageTitle() {
      return 'Uso atual';
    },
    limitLabel() {
      if (this.entitlementData.limit === null) return 'Ilimitado';

      return formatValue(this.entitlementData.limit);
    },
    limitTitle() {
      return 'Limite atual';
    },
    suggestedPlanTitle() {
      return 'Plano sugerido';
    },
    backLabel() {
      return 'Voltar';
    },
    showUsage() {
      return (
        isPresent(this.entitlementData.usage) ||
        isPresent(this.entitlementData.limit)
      );
    },
    canShowProgress() {
      const { usage, limit } = this.entitlementData;

      return Number.isFinite(usage) && Number.isFinite(limit) && limit > 0;
    },
    progressPercent() {
      if (!this.canShowProgress) return 0;

      const percent =
        (this.entitlementData.usage / this.entitlementData.limit) * 100;
      return Math.min(Math.max(percent, 0), 100);
    },
    progressStyle() {
      return {
        width: `${this.progressPercent}%`,
      };
    },
    upgradeDescription() {
      if (this.isEnterpriseSuggestion) {
        return 'Contratacao com suporte para ajustar cota, recorrencia e valores.';
      }

      if (this.suggestedUpgradePlanLabel) {
        return `O plano ${this.suggestedUpgradePlanLabel} aumenta a cota deste recurso.`;
      }

      return null;
    },
    ctaLabel() {
      if (this.isExpired) return 'Ir para faturamento';
      if (this.isEnterpriseSuggestion) return 'Falar com suporte';

      return 'Ver upgrade';
    },
    billingPath() {
      const params =
        !this.isExpired && this.suggestedUpgradePlan
          ? { plan: this.suggestedUpgradePlan }
          : undefined;

      return frontendURL(`accounts/${this.accountId}/settings/billing`, params);
    },
  },
  methods: {
    close() {
      this.$emit('close');
    },
    goToBilling() {
      this.close();

      if (this.$router) {
        this.$router.push(this.billingPath).catch(() => {});
        return;
      }

      window.location.href = this.billingPath;
    },
  },
};
</script>

<template>
  <!-- z-index de 100000 garante que o modal aparece sempre na frente de tudo,
       inclusive de date pickers (z-[99999]) e outros modais. Alerta crítico
       de faturamento deve ser bem visível. -->
  <Modal :show="show" :on-close="close" size="max-w-[34rem]" :z-index="100000">
    <section class="p-6">
      <div class="flex items-start gap-4 pr-8">
        <span
          class="flex items-center justify-center flex-shrink-0 w-10 h-10 rounded-md bg-amber-50 text-amber-800"
        >
          <fluent-icon :icon="isExpired ? 'wallet' : 'lock-closed'" size="20" />
        </span>
        <div class="min-w-0">
          <p class="mb-1 text-sm font-medium text-muted">
            {{ featureLabel }}
          </p>
          <h2 class="text-lg font-semibold m-0 text-body">
            {{ title }}
          </h2>
        </div>
      </div>

      <p class="mt-4 mb-0 text-sm leading-6 text-muted">
        {{ message }}
      </p>

      <dl
        v-if="showUsage"
        class="grid grid-cols-2 gap-4 py-4 mt-5 border-y border-line-faint"
      >
        <div>
          <dt class="text-xs font-medium text-subtle">
            {{ usageTitle }}
          </dt>
          <dd class="mt-1 mb-0 text-base font-semibold text-body">
            {{ usageLabel }}
          </dd>
        </div>
        <div>
          <dt class="text-xs font-medium text-subtle">
            {{ limitTitle }}
          </dt>
          <dd class="mt-1 mb-0 text-base font-semibold text-body">
            {{ limitLabel }}
          </dd>
        </div>
        <div
          v-if="canShowProgress"
          class="col-span-2 h-2 overflow-hidden rounded-sm bg-surface-hover"
        >
          <div class="h-full bg-amber-500" :style="progressStyle" />
        </div>
      </dl>

      <div
        v-if="hasSuggestedUpgrade && upgradeDescription"
        class="pl-4 mt-5 border-l-2 border-woot-500"
      >
        <p class="mb-1 text-xs font-medium text-subtle">
          {{ suggestedPlanTitle }}
        </p>
        <p class="m-0 text-sm leading-6 text-muted">
          {{ upgradeDescription }}
        </p>
      </div>

      <div class="flex flex-col gap-3 mt-6 sm:flex-row sm:justify-end">
        <woot-button
          variant="clear"
          color-scheme="secondary"
          icon="dismiss"
          @click="close"
        >
          {{ backLabel }}
        </woot-button>
        <woot-button icon="arrow-right" @click="goToBilling">
          {{ ctaLabel }}
        </woot-button>
      </div>
    </section>
  </Modal>
</template>
